import type { Site } from '@hotjar/site';
import { useCurrentSite } from '@hotjar/site';
import { supportsMultipleSku } from '@hotjar/webapp/domains/common/product/features';
import { useCurrentSiteAccount } from '@hotjar/account';
import type { ResponseLimitInfo } from '../domain/common/Responses/ResponseLimitInfo';
import { convertMonthStampToDate } from '../domain/common/Responses/ResponseLimitInfo';

import { useResponseLimitInfoQuery } from '..';

export type UseResponsesLimit = {
  responseLimitInfo: ResponseLimitInfo | undefined;
  hasResponsesAvailable: boolean;
  rolloverDate: Date | null;
  isError: boolean;
};

export const useResponsesLimit = (isEnabled?: boolean, customSite?: Site): UseResponsesLimit => {
  const { data: currentSite } = useCurrentSite();

  const { data: account } = useCurrentSiteAccount();
  const hasAskAsASeparateProduct = supportsMultipleSku(account);

  const { data: limitInfo, error } = useResponseLimitInfoQuery({
    site: customSite ?? currentSite,
    isEnabled: isEnabled !== false && hasAskAsASeparateProduct,
  });

  // if the feature flag is not set, the user is on the old SKU and effectively has unlimited responses
  if (!hasAskAsASeparateProduct) {
    return {
      responseLimitInfo: {
        unlimited: true,
      },
      hasResponsesAvailable: true,
      rolloverDate: null,
      isError: false,
    };
  }

  if (limitInfo === undefined) {
    return {
      responseLimitInfo: undefined,
      hasResponsesAvailable: true,
      rolloverDate: null,
      isError: Boolean(error),
    };
  }

  const hasFullInfo = 'time_period' in limitInfo;

  const hasResponsesAvailable = limitInfo.unlimited || limitInfo.remaining > 0 || !hasFullInfo;

  const rolloverDate = hasFullInfo ? convertMonthStampToDate(limitInfo.time_period) : null;

  return {
    responseLimitInfo: limitInfo,
    rolloverDate,
    hasResponsesAvailable,
    isError: Boolean(error),
  };
};
